import React from 'react';
import { Flex } from 'antd';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import { Resource } from '../../Constants/socket';
import useChannelFetching from './hooks/useChannelFetching';
import useChannelActions from './hooks/useChannelActions';
import useChannelSocketEvents from './hooks/useChannelSocketEvents';
import useChannelModals from './hooks/useChannelModals';
import ChannelGrid from './ChannelGrid/ChannelGrid';
import AddChannelModal from './AddChannelModal/AddChannelModal';
import ChannelDetailsModal from './ChannelDetailsModal/ChannelDetailsModal';

const Channels = () => {
    const { isDarkMode } = useTheme();
    const { handleError } = useErrorHandler();
    const { user: { id: currentUserId } = {} } = useGlobal();

    // Subscribe to user-scoped channel for real-time updates
    useSocketSubscription(Resource.USER, currentUserId ? [currentUserId] : null, true);

    // Custom Hooks
    const {
        channels,
        setChannels,
        loading,
        isInitialLoading,
        pagination,
        searchString,
        filters,
        sorter,
        refetchChannels,
        handleTableChange,
        handleSearch,
        handleFilterChange,
    } = useChannelFetching(handleError);

    const {
        handleCreate,
        handleUpdate,
        handleDelete,
        createLoading,
        updateLoading,
        deleteLoading,
    } = useChannelActions(refetchChannels, handleError);

    const {
        highlightedRowIds,
        softRefreshKey,
    } = useChannelSocketEvents(channels, setChannels);

    const {
        showAddModal,
        showDetailsModal,
        selectedChannel,
        handleShowAdd,
        handleShowDetails,
        handleCloseModals,
    } = useChannelModals();

    const handleAction = (action, record, data) => {
        switch (action) {
            case 'view':
                handleShowDetails(record);
                break;
            case 'delete':
                handleDelete(record.id);
                break;
            case 'toggleSync':
                handleUpdate(record.id, data);
                break;
            default:
                break;
        }
    };

    return (
        <Flex
            vertical
            style={{
                flexGrow: 1,
                padding: 0
            }}
        >
            <ChannelGrid
                channels={channels}
                loading={loading}
                isInitialLoading={isInitialLoading}
                pagination={pagination}
                searchString={searchString}
                filters={filters}
                sorter={sorter}
                onTableChange={handleTableChange}
                onSearch={handleSearch}
                onFilterChange={handleFilterChange}
                onAction={handleAction}
                onAdd={handleShowAdd}
                highlightedRowIds={highlightedRowIds}
                softRefreshKey={softRefreshKey}
                deleteLoading={deleteLoading}
                updateLoading={updateLoading}
            />

            {showAddModal && (
                <AddChannelModal
                    onCancel={handleCloseModals}
                    onCreate={handleCreate}
                    loading={createLoading}
                />
            )}

            {showDetailsModal && selectedChannel && (
                <ChannelDetailsModal
                    channel={selectedChannel}
                    onCancel={handleCloseModals}
                />
            )}
        </Flex>
    );
};

export default Channels;
